import React from "react";

const ORIGIN = typeof window !== "undefined" ? window.location.origin : "https://www.hbhslaw.com";

function officeAddress(o) {
  return {
    "@type": "PostalAddress",
    streetAddress: o.street,
    addressLocality: o.city,
    addressRegion: "ID",
    postalCode: o.zip,
    addressCountry: "US",
  };
}

export function firmSchema(firm, attorneys = [], practiceAreas = []) {
  if (!firm) return null;
  return {
    "@context": "https://schema.org",
    "@type": ["LegalService", "Attorney"],
    "@id": `${ORIGIN}/#organization`,
    name: firm.name,
    alternateName: firm.short,
    url: `${ORIGIN}/`,
    logo: `${ORIGIN}/assets/hbhs-logo-clean.png`,
    image: `${ORIGIN}/assets/hbhs-logo-clean.png`,
    telephone: firm.phones?.[0],
    faxNumber: firm.fax,
    email: firm.email,
    priceRange: "$$",
    address: (firm.offices || []).map(officeAddress),
    areaServed: [
      { "@type": "State", name: "Idaho" },
      { "@type": "City", name: "Nampa" },
      { "@type": "City", name: "Meridian" },
      { "@type": "City", name: "Boise" },
      { "@type": "City", name: "Caldwell" },
    ],
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "08:00",
      closes: "17:00",
    },
    knowsAbout: practiceAreas.map((a) => a.title),
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Practice Areas",
      itemListElement: practiceAreas.map((a) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: a.title, url: `${ORIGIN}/practice-areas/${a.id}`, description: a.summary },
      })),
    },
    employee: attorneys.map((a) => ({
      "@type": "Person",
      name: a.name,
      jobTitle: a.title,
      image: `${ORIGIN}${a.image}`,
      worksFor: { "@id": `${ORIGIN}/#organization` },
      alumniOf: (a.education || []).map((e) => ({ "@type": "EducationalOrganization", name: e })),
      knowsAbout: a.practiceAreas,
    })),
  };
}

export function practiceAreaSchema(area, firm, attorneys = []) {
  const url = `${ORIGIN}/practice-areas/${area.id}`;
  const graph = [
    {
      "@type": "Service",
      "@id": `${url}#service`,
      name: area.title,
      serviceType: area.title,
      description: area.description,
      url,
      provider: { "@id": `${ORIGIN}/#organization`, "@type": "LegalService", name: firm?.name, telephone: firm?.phones?.[0], url: `${ORIGIN}/` },
      areaServed: { "@type": "State", name: "Idaho" },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "Practice Areas", item: `${ORIGIN}/#practice` },
        { "@type": "ListItem", position: 3, name: area.title, item: url },
      ],
    },
  ];
  if (area.page?.faqs?.length) {
    graph.push({
      "@type": "FAQPage",
      mainEntity: area.page.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }
  return { "@context": "https://schema.org", "@graph": graph };
}

export default function StructuredData({ data }) {
  if (!data) return null;
  // Rendered as a text node (no innerHTML). Escape characters that could terminate the script tag.
  const json = JSON.stringify(data).replace(/</g, "\\u003c").replace(/\u2028/g, "\\u2028").replace(/\u2029/g, "\\u2029");
  return <script type="application/ld+json">{json}</script>;
}
